import { create } from 'zustand'

import {
  getAIItemDescription,
  getAIProductPhotosList,
} from '@flecto/api/shared'
import { uploadImageByUrl } from '@flecto/utils'

import {
  GRID_SIZE,
  useGridItemPhotosManagerState,
} from '../components/GridItemPhotosManager/useGridItemPhotosManagerState'

export type ItemGeneratorAIState = {
  isLoading: {
    readonly all: boolean
    readonly category: boolean
    readonly description: boolean
    readonly images: boolean
    readonly single: boolean
  }
  isLoadingCategory: boolean
  isLoadingDescription: boolean
  isLoadingImageList: boolean

  imagesLoading: number[]
  abortController: AbortController | null
  setAbortController: (controller: AbortController | null) => void
  cancelGenerateAI: () => void
  isLoadingImageSingle: (index: number) => boolean
  setIsLoadingImageSingle: (index: number, loading: boolean) => void

  getDescriptionWithAI: (
    title: string,
    onUpdateContent?: (message: string) => void,
    onSuccess?: () => void
  ) => void
  getImageWithAI: (
    title: string,
    index: number
  ) => Promise<{ id: string; urlTemporary: string }>
  getImageListWithAI: (
    title: string
  ) => Promise<{ id: string; urlTemporary: string }[]>
  generateListPhotosWithAI: (title: string) => Promise<void>
}

/**
 * This hook exposes a set of functions to get or generate item information,
 * mostly based to its title. Those functions ONLY return content (description,
 * item photos, etc.). The item update IS NOT placed here.
 */
export const useItemGeneratorAI = create<ItemGeneratorAIState>((set, get) => {
  let offset = 0

  return {
    isLoading: {
      get all() {
        return (
          get().isLoadingCategory ||
          get().isLoadingDescription ||
          get().isLoadingImageList
        )
      },
      get category() {
        return get().isLoadingCategory
      },
      get description() {
        return get().isLoadingDescription
      },
      get images() {
        return get().isLoadingImageList
      },
      get single() {
        return get().imagesLoading.length > 0
      },
    },

    isLoadingImageList: false,
    isLoadingCategory: false,
    isLoadingDescription: false,
    imagesLoading: [],

    abortController: null,

    setAbortController: (controller: AbortController | null) => {
      set({ abortController: controller })
    },

    cancelGenerateAI: () => {
      const abortController = get().abortController
      if (abortController) {
        abortController.abort()
        set({
          isLoadingDescription: false,
          isLoadingImageList: false,
          isLoadingCategory: false,
          imagesLoading: [],
          abortController: null,
        })
      }
    },

    isLoadingImageSingle: (index: number) => {
      const loading = get().imagesLoading.find((order) => order === index)
      return typeof loading === 'number' && loading <= 0
        ? true
        : Boolean(loading)
    },

    setIsLoadingImageSingle: (index: number, loading: boolean) => {
      const loadingSinglePhotoWithAI = get().imagesLoading
      let newLoading = [...loadingSinglePhotoWithAI]

      if (!loading) {
        newLoading = newLoading.filter((order) => order !== index)
      } else if (!newLoading.find((order) => order === index)) {
        newLoading.push(index)
      }

      set({ imagesLoading: newLoading })
    },

    getDescriptionWithAI: (title, onUpdateContent, onSuccess) => {
      const abortController = get().abortController || new AbortController()
      set({ isLoadingDescription: true, abortController })

      const errorMessage = 'Error getting item description with AI.'

      getAIItemDescription(title, {
        signal: abortController.signal,
        onmessage: (event) => {
          onUpdateContent?.(event.data as string)
        },
        onclose: () => {
          set({ isLoadingDescription: false })
          onSuccess?.()
        },
        onerror: () => {
          set({ isLoadingDescription: false })
          throw Error(errorMessage)
        },
      }).catch(() => {
        set({ isLoadingDescription: false })
        throw Error(errorMessage)
      })
    },

    getImageWithAI: async (title, index) => {
      const abortController = get().abortController || new AbortController()
      const { images, total } = await getAIProductPhotosList(
        { title },
        {
          signal: abortController.signal,
          offset,
          limit: GRID_SIZE,
        }
      )

      if (total && total > offset) {
        offset += 1
      } else {
        offset = 1
      }

      const imageUrl = images[index] || images[0]

      if (imageUrl) {
        return await uploadImageByUrl(imageUrl, abortController.signal)
      }

      return { id: '', urlTemporary: '' }
    },

    getImageListWithAI: async (title) => {
      const abortController = get().abortController || new AbortController()

      const { images, total } = await getAIProductPhotosList(
        { title },
        {
          signal: abortController.signal,
          offset,
          limit: GRID_SIZE,
        }
      )

      if (total && total > offset) {
        offset += GRID_SIZE
      } else {
        offset = GRID_SIZE
      }

      const promises = []

      for (let i = 0; i < GRID_SIZE; i++) {
        const callback = () => {
          const imageUrl = images[i]
          return uploadImageByUrl(imageUrl, abortController.signal)
        }

        promises.push(callback())
      }

      return Promise.all(promises)
    },

    generateListPhotosWithAI: (title) => {
      return useGridItemPhotosManagerState
        .getState()
        .generateListPhotosWithAI(title)
    },
  }
})
